/** Device-local quest signals. */

const OPENED_PUSH_KEY = "um.quests.openedPush";

export function stampNotificationOpened(): void {
  try {
    localStorage.setItem(OPENED_PUSH_KEY, new Date().toISOString());
  } catch {
    // Non-fatal: the quest simply stays incomplete.
  }
}

/** Only used by the dev reset — see `devReset.ts`. */
export function clearNotificationOpened(): void {
  try {
    localStorage.removeItem(OPENED_PUSH_KEY);
  } catch {
    // Non-fatal: the quest simply stays complete.
  }
}

export function hasOpenedNotification(): boolean {
  try {
    return !!localStorage.getItem(OPENED_PUSH_KEY);
  } catch {
    return false;
  }
}

/** **Temporary dev affordance** — deleted with `devReset.ts`. */
export type DevQuestOverride = "all" | "none";

const DEV_OVERRIDE_KEY = "um.quests.devOverride";

/**
 * `useQuests` runs in several places at once — home card, quests modal,
 * achievement banner. Subscribers are what keep them agreeing; without them a
 * toggle only updates the surface it was pressed on.
 */
const listeners = new Set<() => void>();

export function subscribeDevQuestFlags(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function setDevQuestOverride(value: DevQuestOverride | null): void {
  try {
    if (value) localStorage.setItem(DEV_OVERRIDE_KEY, value);
    else localStorage.removeItem(DEV_OVERRIDE_KEY);
  } catch {
    // Non-fatal: the ladder just stays as it was.
  }
  for (const listener of listeners) listener();
}

export function devQuestOverride(): DevQuestOverride | null {
  try {
    const raw = localStorage.getItem(DEV_OVERRIDE_KEY);
    return raw === "all" || raw === "none" ? raw : null;
  } catch {
    return null;
  }
}

/** **Temporary dev affordance.** Un-retires a finished ladder. */
const DEV_UNRETIRE_KEY = "um.quests.devUnretire";

export function setDevUnretired(value: boolean): void {
  try {
    if (value) localStorage.setItem(DEV_UNRETIRE_KEY, "1");
    else localStorage.removeItem(DEV_UNRETIRE_KEY);
  } catch {
    // Non-fatal: the ladder just stays retired.
  }
  for (const listener of listeners) listener();
}

export function devUnretired(): boolean {
  try {
    return localStorage.getItem(DEV_UNRETIRE_KEY) === "1";
  } catch {
    return false;
  }
}
